import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, type QueryConstraint } from "firebase/firestore";
import { db } from "@/services/firebase";

/**
 * A Firestore collection, live.
 *
 * Read directly rather than through a callable. The rules already say
 * `isSpontarAdmin()` for everything this console reads, and a callable in front
 * of them would be a second authorisation path for a decision the rules have
 * already made — two places to keep in agreement, and one of them will
 * eventually be wrong.
 */
export function useCollection<T>(
  path: string,
  constraints: readonly QueryConstraint[] = [],
  enabled = true
): { rows: T[]; loading: boolean; failed: boolean } {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  // Serialised so a fresh array literal at the call site does not resubscribe
  // on every render — the mistake that turns a listener into a request storm.
  const key = JSON.stringify(constraints.map((constraint) => constraint.type));

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setFailed(false);

    return onSnapshot(
      constraints.length > 0
        ? query(collection(db(), path), ...constraints)
        : collection(db(), path),
      (snapshot) => {
        setRows(snapshot.docs.map((document) => document.data() as T));
        setLoading(false);
      },
      () => {
        setRows([]);
        setFailed(true);
        setLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key, enabled]);

  return { rows, loading, failed };
}

/** Everyone in one institution. */
export function useInstitutionUsers<T>(institutionId: string) {
  return useCollection<T>("users", [where("institutionId", "==", institutionId)], institutionId !== "");
}
